// Личные данные. Одна правка — имя; всё остальное показывается.
//
// Экран завёлся 14 сентября 2026 по простому поводу: в приветствии стояло имя
// из тестовой строки базы, и поправить его было нечем — только `npx convex
// run` из терминала. Обращаться к ученику словом, которое он не может
// переписать, — это ошибка, которую видно каждый день в первой строке экрана.
//
// Настройками страница не становится и не станет. Настройка меняет поведение
// системы, а здесь меняется одно слово в шапке. Что повторять, что открыть,
// сколько задач в дне — по-прежнему решает система, и ручек для этого нет
// намеренно (решение 2: единственная точка автономии — обязательство, и
// размывать её списком тумблеров нельзя). Поэтому экзамен, пояс и
// обязательство здесь стоят фактами, а не полями: ученик видит, из чего
// система считает дни, и не гадает.

import { useEffect, useState } from "react";
import { api, useMutation, useQuery } from "../api";
import { TargetPick } from "./TargetPick";
import { formatDay, plural } from "../lib/dates";

/** «16 июня 2027». Год нужен: до экзамена больше года, и без него дата врёт. */
function withYear(date: string): string {
  return `${formatDay(date)} ${date.slice(0, 4)}`;
}

/**
 * Момент в «ГГГГ-ММ-ДД» по МЕСТНЫМ часам, а не по UTC. `toISOString().slice(0, 10)`
 * стоял здесь и сдвигал дату заведения на сутки у всех, кто завёлся вечером:
 * Минск — UTC+3, и всё после 21:00 по-местному в UTC уже завтра.
 */
function localDate(ms: number): string {
  const d = new Date(ms);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export function Profile({ learnerKey }: { learnerKey: string }) {
  // Узкий срез: одна строка `learners`. Подписка перезапускается только когда
  // правили саму строку, то есть на этом же экране.
  const profile = useQuery(api.profile.current, { learnerKey });
  const setName = useMutation(api.profile.setName);
  // Цель подготовки (30 октября 2026) — из той же строки `learners`.
  const goal = useQuery(api.goal.current, { learnerKey });
  const [changingTarget, setChangingTarget] = useState(false);

  const [draft, setDraft] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  // Черновик заводится из подписки один раз — пока его не трогали. Класть в
  // него каждое обновление подписки нельзя: собственный ответ сервера тогда
  // затирал бы то, что ученик печатает прямо сейчас.
  useEffect(() => {
    if (profile === undefined) return;
    setDraft((d) => (d === null ? profile.name : d));
  }, [profile]);

  if (changingTarget && goal !== undefined) {
    return (
      <div className="cols cols--solo">
        <div className="col">
          <TargetPick
            learnerKey={learnerKey}
            change
            initial={{ target: goal.target, targetName: goal.targetName, examDate: goal.examDate }}
            onDone={() => setChangingTarget(false)}
            onCancel={() => setChangingTarget(false)}
          />
        </div>
      </div>
    );
  }

  if (profile === undefined) {
    return (
      <div className="cols cols--solo">
        <div className="col">
          <div className="skeleton" style={{ width: 180 }} />
          <div className="card" style={{ marginTop: 16 }}>
            <div className="skeleton" />
          </div>
        </div>
      </div>
    );
  }

  const value = draft ?? profile.name;
  // Сравнение с тем, что записано, идёт по сырому черновику: нормализация
  // живёт на сервере, и повторять её здесь значило бы завести второй источник
  // правила. Цена — кнопка, живая при одном лишнем пробеле; сервер на ней
  // запишет то же самое, и экран покажет нормализованный результат.
  const changed = value !== profile.name;

  const save = () => {
    setBusy(true);
    setFailed(null);
    setSaved(false);
    void setName({ learnerKey, name: value })
      .then((r) => {
        setBusy(false);
        // Показывается то, что записано, а не то, что ввели: пробелы срезаны,
        // длина обрезана, и увидеть это ученик должен сразу.
        setDraft(r.name);
        setSaved(true);
      })
      .catch((e: unknown) => {
        setBusy(false);
        setFailed(e instanceof Error ? e.message : String(e));
      });
  };

  return (
    <div className="cols cols--solo">
      <div className="col">
        {/*
          Экран начинается полем. Над ним стояли три строки, и ни одна не
          отвечала на вопрос, с которым сюда приходят: название экзамена
          (оно есть на «Сегодня»), заголовок «Личные данные» (он в шапке
          приложения) и абзац о том, что имя ни на что не влияет. Правило,
          которое и правда нужно знать, — предел длины — стоит под полем, там,
          где в него упираются.
        */}
        <div className="card">
          <div className="card__label">Как обращаться</div>
          <div className="row" style={{ marginTop: 14, gap: 10 }}>
            <input
              className="field field--text"
              value={value}
              // Предел держит браузер, а не подсветка поля. Красная рамка на
              // переполнении стояла здесь и не загоралась ни разу: maxLength
              // не даёт набрать лишнего, и проверять было нечего. Вставленное
              // из буфера длинное имя браузер обрежет молча — о пределе
              // сказано строкой ниже, до того, как в него упрутся.
              maxLength={profile.nameLimit}
              autoComplete="given-name"
              spellCheck={false}
              placeholder="Имя"
              onChange={(event) => {
                setDraft(event.target.value);
                setSaved(false);
              }}
              onKeyDown={(event) => {
                if (event.key === "Enter" && changed && !busy) save();
              }}
            />
            <button
              className="btn btn--primary"
              disabled={!changed || busy}
              onClick={save}
            >
              {busy ? "Записываю" : "Сохранить"}
            </button>
          </div>

          <div className="faint" style={{ marginTop: 14, lineHeight: 1.6 }}>
            До <span className="num">{profile.nameLimit}</span> знаков: дальше
            приветствие занимает пол-экрана. Пустое поле — тоже ответ, тогда
            система здоровается без обращения.
          </div>

          {failed === null ? null : (
            <div className="notice" style={{ marginTop: 14 }}>
              {failed}
            </div>
          )}

          {saved && !changed ? (
            <div className="faint" style={{ marginTop: 10 }}>
              Записано{value === "" ? " — приветствие пойдёт без имени" : ""}.
            </div>
          ) : null}
        </div>

        {/*
          Цель подготовки — выбор, а не факт: меняется отдельным действием с
          предупреждением, потому что цель и дата пересчитывают план и темп
          (решение 30 октября 2026).
        */}
        {goal === undefined ? null : (
          <div className="card" style={{ marginTop: 12 }}>
            <div className="card__label">Цель подготовки</div>
            <div className="row" style={{ marginTop: 12, gap: 12, justifyContent: "space-between", flexWrap: "wrap" }}>
              <div>
                <div style={{ fontWeight: 600 }}>{goal.examTitle}</div>
                <div className="muted" style={{ fontSize: 14 }}>
                  {withYear(goal.examDate)}
                </div>
              </div>
              <button type="button" className="btn" onClick={() => setChangingTarget(true)}>
                Сменить цель
              </button>
            </div>
          </div>
        )}

        {/*
          Факты, а не поля. Дата экзамена — то, от чего считаются все интервалы
          повторения, и правка её переносит каждый срок в системе; пояс
          меняется переездом, а не решением; обязательство берётся один раз
          после диагностики и переписывается не чаще раза в неделю — там же,
          где бралось. Здесь они стоят затем, чтобы ученик видел, из чего
          система считает его дни.
        */}
        <div className="card" style={{ marginTop: 12 }}>
          <div className="card__label">Из чего считаются дни</div>
          <div
            className="muted"
            style={{ marginTop: 12, fontSize: 14, lineHeight: 1.8 }}
          >
            <div>
              Экзамен — {withYear(profile.examDate)}
            </div>
            <div>
              Обязательство —{" "}
              {profile.daysPerWeek === null ? (
                "ещё не взято"
              ) : (
                <>
                  <span className="num">{profile.daysPerWeek}</span>{" "}
                  {plural(profile.daysPerWeek, "день", "дня", "дней")} в неделю
                </>
              )}
            </div>
            <div>Часовой пояс — {profile.timeZone}</div>
            <div>В системе с {withYear(localDate(profile.createdAt))}</div>
          </div>
          <div className="faint" style={{ marginTop: 14, lineHeight: 1.6 }}>
            Отсюда они не правятся. Дату экзамена меняет «Сменить цель» выше —
            от неё считаются план и темп. Обязательство берётся один раз,
            переписать его можно не чаще раза в неделю.
          </div>
        </div>

        {/*
          Кнопки «К набору дня» внизу больше нет: 24 сентября 2026 выход со
          всех экранов свели к шеврону в шапке, и здесь она была третьим
          способом сделать одно и то же — после шеврона и имени продукта.
        */}
      </div>
    </div>
  );
}
